<template>
  <v-data-table :headers="headers" :items="entries" :pagination.sync="pagination" item-key="id"
    class="audit-log-table"
    :custom-filter="filteredEntries"
    :search="''"
    :rows-per-page-items="[10]"
  >
    <template v-slot:headers="props">
      <tr class="table-header-row">
        <th v-for="header in props.headers" :key="header.value" 
          :class="[
            'column', 
            header.align === 'center' ? 'text-xs-center' : 'text-xs-left',
            header.sortable !== false ? 'sortable' : '',
            pagination.descending ? 'desc' : 'asc', 
            header.value === pagination.sortBy ? 'active' : ''
          ]"
          :style="{ 
            display: header.hidden === true ? 'none' : 'table-cell',
            width: header.width,
          }"
        >
          <div class="table-header" @click="changeSort(header)">
            {{ header.text }}
            <v-icon v-if="header.sortable !== false" small>arrow_upward</v-icon>
          </div>
          <table-filter-text-search v-if="header.type === 'textsearch'"
            :value="header.search"
            :placeholder="header.placeholder"
            @changed="header.changed"
          />
          <table-filter-multi-select v-if="header.type === 'multiselect'"
            :options="header.options"
            :values="header.selectedValues"
            :placeholder="header.placeholder"
            @selected="header.selected"
            @cleared="header.cleared"
          />
          <table-filter-date v-if="header.type === 'date'"
            :value="header.filter"
            :placeholder="header.placeholder"
            :language="header.language"
            @changed="header.changed"
          />
        </th>
      </tr>
    </template>
    <template v-slot:items="props">
      <tr :key="props.item.id">
        <td>{{ props.item.dateTimeFormatted }}</td>
        <td>{{ props.item.userIdentifier }}</td>
        <td>{{ props.item.actionTypeText }}</td>
        <td>{{ props.item.actionText }}</td>
        <td>{{ props.item.actionDetails }}</td>
      </tr>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { namespace } from 'vuex-class'

import TableFilterDate from '../../../../ui/TableFilterDate.vue'
import TableFilterMultiSelect from '../../../../ui/TableFilterMultiSelect.vue'
import TableFilterTextSearch from '../../../../ui/TableFilterTextSearch.vue'

import { AuditLogEntryViewModel, AuditLogTableFilterSettings } from '../../../../../store/modules/auditLogUi/models'

import { DropdownOption } from '../../../../../../../shared-components/src'

const App = namespace('app')
const Resource = namespace('resource')
const AuditLogUi = namespace('auditLogUi')

@Component({
  components: {
    TableFilterDate,
    TableFilterMultiSelect,
    TableFilterTextSearch,
  },
})
export default class AuditLogTable extends Vue {
  @AuditLogUi.Getter public isInteracted: boolean
  @AuditLogUi.Getter public filterSettings: AuditLogTableFilterSettings
  @AuditLogUi.Getter public entries: AuditLogEntryViewModel[]
  @AuditLogUi.Getter public filteredEntries: (entries: AuditLogEntryViewModel[]) => AuditLogEntryViewModel[]
  @AuditLogUi.Getter public actionTypeOptions: DropdownOption
  @AuditLogUi.Action public updateFilterSettings: (payload: { property: string, value: any }) => Promise<void>
  
  @App.Getter public selectedLanguage: string

  @Resource.Getter public dictionary

  public pagination = {
    sortBy: 'timeStampInUtc',
    descending: true,
    rowsPerPage: 10,
  }

  public changeSort(column) {
    if (column.sortable === false) {
      return
    }
    if (this.pagination.sortBy === column.value) {
      this.pagination.descending = !this.pagination.descending
    } else {
      this.pagination.sortBy = column.value
      this.pagination.descending = false
    }
  }

  public get headers() {
    return [
      { 
        text: this.dictionary('alarm.timestamp'),
        value: 'timeStampInUtc',
        type: 'date',
        filter: this.filterSettings.date,
        placeholder: this.dictionary('common.filter'),
        changed: (filter: string) => this.updateFilterSettings({ 
          property: 'date',
          value: filter,
        }),
        language: this.selectedLanguage,
        align: 'left',
        sortable: true,
        width: '15%',
      },
      {
        text: this.dictionary('auditLog.user'),
        value: 'userIdentifier',
        type: 'textsearch',
        search: this.filterSettings.userIdentifier,
        placeholder: this.dictionary('common.search'),
        changed: (searchString: string) => this.updateFilterSettings({ 
          property: 'userIdentifier',
          value: searchString,
        }),
        width: '15%',
      },    
      { 
        text: this.dictionary('auditLog.actionTypes'),
        value: 'actionTypeText',
        type: 'multiselect',
        options: this.actionTypeOptions,
        selectedValues: this.filterSettings.actionTypes,
        placeholder: this.dictionary('common.filter'),
        selected: (values: boolean[]) => this.updateFilterSettings({ 
          property: 'actionTypes',
          value: values,
        }),
        cleared: () => this.updateFilterSettings({ 
          property: 'actionTypes',
          value: [],
        }),
        width: '15%',
      },
      {
        text: this.dictionary('auditLog.actionText'),
        value: 'actionText',
        type: 'textsearch',
        search: this.filterSettings.actionText,
        placeholder: this.dictionary('common.search'),
        changed: (searchString: string) => this.updateFilterSettings({ 
          property: 'actionText',
          value: searchString,
        }),
        width: '20%',
      },
      {
        text: this.dictionary('auditLog.actionDetails'),
        value: 'actionDetails',
        type: 'textsearch',
        search: this.filterSettings.actionDetails,
        placeholder: this.dictionary('common.search'),
        changed: (searchString: string) => this.updateFilterSettings({ 
          property: 'actionDetails',
          value: searchString,
        }),
        width: '35%',
      },
    ]
  }
}
</script>
<style lang="scss" scoped>
th, td {
  padding: 0px 15px !important;
  overflow: hidden;
  text-overflow: ellipsis;
}
thead tr {
  border-bottom: 1px solid white!important;
}
th:first-child, td:first-child {
  padding-left: 0px !important;
}
.table-header-row {
  vertical-align: top;
}
.table-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
}
tr.selected {
  background: #c2043e !important;
}
</style>
<style lang="css">
.audit-log-table .theme--dark.v-table {
  background-color: transparent !important;
  table-layout: fixed;
  width: 100%;
}
.audit-log-table .v-datatable__actions {
  background-color: transparent !important;
}
.audit-log-table .autocomplete-input input{
  cursor: pointer !important;
}
</style>